import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { clearNulls } from "@/services/clearNulls";
import { ChevronRight } from "@carbon/icons-react";
import { t } from "@lingui/macro";
import { Box, IconButton, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
  ImperialMetricDisplayPreference,
  WahooWorkoutTypeFamily,
  WorkoutAlertInputType,
  WorkoutAlertTriggerTypeEnum,
  WorkoutProfileType,
} from "@WahooFitness/cloud-client-types";
import {
  BlueSwitch,
  DeleteIcon,
  MenuListItemType,
  MenuListItemVariant,
} from "@WahooFitness/redesignr";
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import AlertIntervalEditor from "./AlertIntervalEditor";
import AlertMetricSelector from "./AlertMetricSelector";
import AlertTriggerSelector from "./AlertTriggerSelector";
import useAlertMetrics from "./useAlertMetrics";
import useAlertTriggers from "./useAlertTriggers";
import { useUserContext, useGuardedUpdater } from "@WahooFitness/wahoo-offline-mfe";
import useAssociatedDevices from "@/hooks/useAssociatedDevices";
import useFlaggedFeatures from "@/hooks/useFlaggedFeatures";
import { FlaggedFeature } from "@/hooks/types/FlaggedFeature";
import { WSMWorkoutAlert } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";

const intervalTriggerTypes = [
  WorkoutAlertTriggerTypeEnum.distance,
  WorkoutAlertTriggerTypeEnum.duration,
  WorkoutAlertTriggerTypeEnum.calories_burned,
];

const useAlertList = (workoutProfileId: string) => {
  const [editing, setEditing] = useState(false);
  const { update, updateInProgress } = useGuardedUpdater();

  const {
    getWorkoutProfile,
    updateProfile,
    mutateProfileState,
    workoutProfilesLoading,
    workoutProfilesError,
    useWorkoutAlerts,
  } = useWorkoutProfilesContext();

  const { workoutAlerts, createWorkoutAlert, deleteWorkoutAlert, editWorkoutAlert } =
    useWorkoutAlerts(workoutProfileId);

  const workoutProfile = useMemo(() => {
    return getWorkoutProfile(workoutProfileId);
  }, [getWorkoutProfile, workoutProfileId]);

  const { hasAssociatedElemnt, hasAssociatedAce, hasPairedGen3ComputerWithSpeaker } =
    useAssociatedDevices();

  const { checkIsFeatureEnabled, featuresAreLoading } = useFlaggedFeatures();

  const showElemntOptions = useMemo(
    () =>
      hasAssociatedElemnt &&
      workoutProfile?.workout_type_family_id === WahooWorkoutTypeFamily.Cycling,
    [hasAssociatedElemnt, workoutProfile?.workout_type_family_id]
  );

  const showGen3DeviceOptionsWithSpeaker = useMemo(
    () =>
      (hasAssociatedAce || hasPairedGen3ComputerWithSpeaker) &&
      workoutProfile?.workout_type_family_id === WahooWorkoutTypeFamily.Cycling,
    [hasAssociatedAce, hasPairedGen3ComputerWithSpeaker, workoutProfile?.workout_type_family_id]
  );

  const showAceOptions = useMemo(
    () =>
      hasAssociatedAce && workoutProfile?.workout_type_family_id === WahooWorkoutTypeFamily.Cycling,
    [hasAssociatedAce, workoutProfile?.workout_type_family_id]
  );

  const customAlerts = useMemo(() => {
    if (workoutProfilesLoading || !!workoutProfilesError || !workoutProfile) {
      return [];
    }
    return workoutAlerts ?? [];
  }, [workoutAlerts, workoutProfile, workoutProfilesError, workoutProfilesLoading]);

  const { currentUser } = useUserContext();

  const distanceUnitPreference = useMemo(
    () =>
      currentUser?.display_preference?.speed_distance === ImperialMetricDisplayPreference.imperial
        ? ImperialMetricDisplayPreference.imperial
        : ImperialMetricDisplayPreference.metric,
    [currentUser?.display_preference?.speed_distance]
  );

  const { getTriggerSummaryDisplayString } = useAlertTriggers(distanceUnitPreference);

  const toggleEditing = useCallback(() => {
    setEditing((value) => !value);
  }, []);

  useEffect(() => {
    if (customAlerts.length === 0) {
      setEditing(false);
    }
  }, [customAlerts.length]);

  const [currentDrawerHeight, setCurrentDrawerHeight] = useState(0);

  /**
   * When editing (or creating) an alert, this stack is used to keep track of the screens
   * viewed during the process, so the back button will work properly.
   * The view at the top of the stack is always the one being shown,
   * and an empty stack means the edit view will not be open
   */
  const [editorStack, setEditorStack] = useState<ReactElement[]>([]);
  const gotoNextEditorView = useCallback(
    (nextEditorView: ReactElement) => setEditorStack((original) => [...original, nextEditorView]),
    []
  );
  const gotoPreviousEditorView = useCallback(
    () => setEditorStack((original) => original.slice(0, -1)),
    []
  );
  const clearEditor = useCallback(() => setEditorStack([]), []);
  const currentEditorView = useMemo(() => editorStack[editorStack.length - 1], [editorStack]);
  const editorOpen = useMemo(() => editorStack.length > 0, [editorStack.length]);

  const saveAlert = useCallback(
    async (alert: WorkoutAlertInputType) => {
      if (!workoutProfile) {
        return false;
      }
      const newAlerts = [...customAlerts];
      if (!alert.external_id) {
        await update(() => createWorkoutAlert(alert), {
          afterUpdate: (savedAlert) => {
            if (!savedAlert) {
              return;
            }
            newAlerts.push(savedAlert);
            mutateProfileState({
              ...workoutProfile,
              workout_alert_ids: newAlerts.map((alert) => alert.id),
              workout_alerts: newAlerts,
            });
          },
        });
      } else {
        await update(() => editWorkoutAlert(alert.external_id ?? "", clearNulls(alert)), {
          afterUpdate: (savedAlert) => {
            if (!savedAlert) {
              return;
            }
            const alertIndex =
              newAlerts?.findIndex((savedAlert) => savedAlert.external_id === alert.external_id) ??
              -1;
            if (alertIndex !== -1) {
              newAlerts[alertIndex] = savedAlert;
            }
            mutateProfileState({
              ...workoutProfile,
              workout_alert_ids: newAlerts.map((alert) => alert.id),
              workout_alerts: newAlerts,
            });
          },
        });
      }
    },
    [createWorkoutAlert, customAlerts, editWorkoutAlert, mutateProfileState, update, workoutProfile]
  );

  const deleteAlert = useCallback(
    async (alert: WSMWorkoutAlert) => {
      await update(() => deleteWorkoutAlert(alert.external_id), {
        afterUpdate: () => {
          const alerts = workoutProfile?.workout_alerts;
          const alertIndex = alerts?.findIndex((savedAlert) => savedAlert.id === alert.id) ?? -1;
          if (alerts && alertIndex !== -1) {
            alerts.splice(alertIndex, 1);
            mutateProfileState({ ...workoutProfile, workout_alerts: [...alerts] });
          }
        },
      });
    },
    [deleteWorkoutAlert, mutateProfileState, update, workoutProfile]
  );

  const toggleEnabled = useCallback(
    (alert: WSMWorkoutAlert) => {
      alert.audio = !alert.audio;
      saveAlert(alert);
    },
    [saveAlert]
  );

  const handleCreateAlert = useCallback(
    (alert: WorkoutAlertInputType) => {
      saveAlert(alert);
      clearEditor();
    },
    [clearEditor, saveAlert]
  );

  const openMetricSelector = useCallback(
    (alert: WorkoutAlertInputType) => {
      gotoNextEditorView(
        <AlertMetricSelector
          alert={alert}
          backAction={gotoPreviousEditorView}
          onCreate={handleCreateAlert}
        />
      );
    },
    [gotoNextEditorView, gotoPreviousEditorView, handleCreateAlert]
  );

  const handleConfirmInterval = useCallback(
    (alert: WorkoutAlertInputType) => {
      openMetricSelector(alert);
    },
    [openMetricSelector]
  );

  const openIntervalEditor = useCallback(
    (alert: WorkoutAlertInputType) => {
      gotoNextEditorView(
        <AlertIntervalEditor
          alert={alert}
          backAction={gotoPreviousEditorView}
          onNext={handleConfirmInterval}
          unitPreference={distanceUnitPreference}
        />
      );
    },
    [distanceUnitPreference, gotoNextEditorView, gotoPreviousEditorView, handleConfirmInterval]
  );

  const handleTriggerSelection = useCallback(
    (alert: WorkoutAlertInputType) => {
      if (alert.trigger_type === undefined) {
        return;
      }
      if (intervalTriggerTypes.includes(alert.trigger_type)) {
        openIntervalEditor(alert);
      } else {
        openMetricSelector(alert);
      }
    },
    [openIntervalEditor, openMetricSelector]
  );

  const openTriggerSelector = useCallback(
    (alert: WorkoutAlertInputType) => {
      gotoNextEditorView(
        <AlertTriggerSelector
          alert={alert}
          backAction={clearEditor}
          onSelection={handleTriggerSelection}
        />
      );
    },
    [gotoNextEditorView, clearEditor, handleTriggerSelection]
  );

  const handleEditAlert = useCallback(
    (alert: WSMWorkoutAlert) => {
      openTriggerSelector({
        ...alert,
        workout_metric_type_ids: [...alert.workout_metric_type_ids],
      });
    },
    [openTriggerSelector]
  );

  const handleAddAlert = useCallback(() => {
    openTriggerSelector({
      trigger_type: 0,
      workout_metric_type_ids: [],
      audio: true,
      visual: false,
      custom_alert_text: "",
      repeat: true,
    });
  }, [openTriggerSelector]);

  const { getAlertMetricSummaryDisplayString } = useAlertMetrics();

  const getAlertInformationStrings = useCallback(
    (alert: WSMWorkoutAlert) => {
      const infoStrings = [];
      const totalAlertCount =
        (alert.workout_metric_type_ids?.length ?? 0) + (alert.custom_alert_text ? 1 : 0);
      if (alert.custom_alert_text) {
        infoStrings.push(t`Custom: ${alert.custom_alert_text}`);
      }
      infoStrings.push(
        ...alert.workout_metric_type_ids
          .slice(0, (totalAlertCount <= 3 ? totalAlertCount : 2) - infoStrings.length)
          .map(getAlertMetricSummaryDisplayString)
      );
      if (totalAlertCount > infoStrings.length) {
        infoStrings.push(t`+${totalAlertCount - infoStrings.length}`);
      }
      return infoStrings;
    },
    [getAlertMetricSummaryDisplayString]
  );

  const mapAlertToListItem = useCallback(
    (alert: WSMWorkoutAlert) => {
      const infoStrings = getAlertInformationStrings(alert);
      return {
        id: alert.id.toString(),
        content: getTriggerSummaryDisplayString(alert),
        secondaryContent: (
          <>
            {infoStrings.map((infoString) => (
              <React.Fragment key={infoString}>
                <Typography
                  variant="inherit"
                  noWrap
                  textOverflow="ellipsis"
                  overflow="hidden"
                  maxWidth="100%"
                >
                  {infoString}
                </Typography>
              </React.Fragment>
            ))}
          </>
        ),
        variant: MenuListItemVariant.Action,
        actionComponent: editing ? (
          <Box display="flex" flexDirection="row" gap={2} alignItems="center">
            <IconButton
              onClick={() => deleteAlert(alert)}
              disabled={updateInProgress}
              data-testid="deleteAlertButton"
            >
              <DeleteIcon color="error" height={24} width={24} />
            </IconButton>
            <IconButton
              onClick={() => handleEditAlert(alert)}
              disabled={updateInProgress}
              data-testid="editAlertButton"
            >
              <ChevronRight height={24} width={24} />
            </IconButton>
          </Box>
        ) : (
          <BlueSwitch checked={alert.audio} data-testid="alertToggle" />
        ),
        disabled: updateInProgress,
        action: editing ? undefined : () => toggleEnabled(alert),
      } as MenuListItemType;
    },
    [
      getAlertInformationStrings,
      getTriggerSummaryDisplayString,
      editing,
      updateInProgress,
      deleteAlert,
      handleEditAlert,
      toggleEnabled,
    ]
  );

  const toggleField = useCallback(
    (fieldName: keyof WorkoutProfileType) => {
      updateProfile(workoutProfileId, fieldName, !workoutProfile?.[fieldName]);
    },
    [updateProfile, workoutProfile, workoutProfileId]
  );

  const generalListTitle = useMemo(
    () =>
      showElemntOptions ? (
        <Box display="flex" flexDirection="column">
          <Typography variant="prose-base">{t`General workout alerts`}</Typography>
          <Typography
            variant="prose-xs"
            color="text.secondary"
          >{t`Workout started, paused, resumed, stopped, finished`}</Typography>
        </Box>
      ) : undefined,
    [showElemntOptions]
  );

  const generalAlertsSecondaryContent = useMemo(() => {
    if (showGen3DeviceOptionsWithSpeaker) return t`Voice`;
    if (showElemntOptions) return undefined;
    return t`Workout started, paused, lap, resumed, stopped, finished`;
  }, [showGen3DeviceOptionsWithSpeaker, showElemntOptions]);

  const generalListItems = useMemo(
    () =>
      [
        showElemntOptions
          ? {
              id: "elemnt-general-alerts",
              content: t`ELEMNT`,
              secondaryContent: showGen3DeviceOptionsWithSpeaker ? t`Beeps` : undefined,
              variant: MenuListItemVariant.Action,
              actionComponent: (
                <BlueSwitch
                  checked={workoutProfile?.alerts_workouts}
                  data-testid="elemntAlertToggle"
                />
              ),
              action: () => {
                toggleField("alerts_workouts");
              },
            }
          : undefined,
        {
          id: "general-alerts",
          content: showElemntOptions ? t`App recording` : t`General workout alerts`,
          secondaryContent: generalAlertsSecondaryContent,
          variant: MenuListItemVariant.Action,
          actionComponent: (
            <BlueSwitch
              checked={workoutProfile?.general_audio_announcements}
              data-testid="generalAlertToggle"
            />
          ),
          action: () => {
            toggleField("general_audio_announcements");
          },
        },
      ].filter(Boolean) as MenuListItemType[],
    [
      generalAlertsSecondaryContent,
      showGen3DeviceOptionsWithSpeaker,
      showElemntOptions,
      toggleField,
      workoutProfile?.alerts_workouts,
      workoutProfile?.general_audio_announcements,
    ]
  );

  const navigationAlertListTitle = useMemo(() => {
    return showElemntOptions ? (
      <Typography variant="prose-base">{t`Turn by turn navigation alerts`}</Typography>
    ) : undefined;
  }, [showElemntOptions]);
  const navigationAlertListItems = useMemo(() => {
    return showElemntOptions
      ? ([
          {
            id: "navigation-alerts",
            content: t`ELEMNT`,
            secondaryContent: showGen3DeviceOptionsWithSpeaker ? (
              <ToggleButtonGroup
                value={workoutProfile?.alerts_routes_use_voice}
                exclusive
                fullWidth
                size="small"
                onChange={(_, value) => {
                  if (value !== null) {
                    updateProfile(workoutProfileId, "alerts_routes_use_voice", value);
                  }
                }}
              >
                <ToggleButton value={true}>{t`Voice`}</ToggleButton>
                <ToggleButton value={false}>{t`Beeps`}</ToggleButton>
              </ToggleButtonGroup>
            ) : undefined,
            variant: showGen3DeviceOptionsWithSpeaker
              ? MenuListItemVariant.Accordion
              : MenuListItemVariant.Action,
            expanded: workoutProfile?.alerts_routes,
            actionComponent: (
              <BlueSwitch
                checked={workoutProfile?.alerts_routes}
                data-testid="elemntNavigationAlertToggle"
              />
            ),
            action: () => {
              toggleField("alerts_routes");
            },
          },
        ] as MenuListItemType[])
      : [];
  }, [
    showGen3DeviceOptionsWithSpeaker,
    showElemntOptions,
    toggleField,
    updateProfile,
    workoutProfile?.alerts_routes,
    workoutProfile?.alerts_routes_use_voice,
    workoutProfileId,
  ]);

  const plannedWorkoutAlertListTitle = useMemo(() => {
    return showElemntOptions ? (
      <Typography variant="prose-base">{t`Planned Workout alerts`}</Typography>
    ) : undefined;
  }, [showElemntOptions]);

  const elemntPlannedWorkoutsEnabled = useMemo(
    () => checkIsFeatureEnabled(FlaggedFeature.ElemntPlannedWorkouts),
    [checkIsFeatureEnabled]
  );
  const plannedWorkoutAlertListItems = useMemo(() => {
    return showElemntOptions
      ? ([
          {
            id: "planned-workout-alerts",
            content: t`ELEMNT`,
            secondaryContent:
              showGen3DeviceOptionsWithSpeaker && elemntPlannedWorkoutsEnabled ? (
                <ToggleButtonGroup
                  value={workoutProfile?.alerts_plans_use_voice}
                  exclusive
                  fullWidth
                  size="small"
                  onChange={(_, value) => {
                    if (value !== null) {
                      updateProfile(workoutProfileId, "alerts_plans_use_voice", value);
                    }
                  }}
                >
                  <ToggleButton value={true}>{t`Voice`}</ToggleButton>
                  <ToggleButton value={false}>{t`Beeps`}</ToggleButton>
                </ToggleButtonGroup>
              ) : undefined,
            variant:
              showGen3DeviceOptionsWithSpeaker && elemntPlannedWorkoutsEnabled
                ? MenuListItemVariant.Accordion
                : MenuListItemVariant.Action,
            expanded: workoutProfile?.alerts_plans,
            actionComponent: (
              <BlueSwitch
                checked={workoutProfile?.alerts_plans}
                data-testid="elemntPlannedWorkoutAlertToggle"
              />
            ),
            action: () => {
              toggleField("alerts_plans");
            },
          },
        ] as MenuListItemType[])
      : [];
  }, [
    elemntPlannedWorkoutsEnabled,
    showGen3DeviceOptionsWithSpeaker,
    showElemntOptions,
    toggleField,
    updateProfile,
    workoutProfile?.alerts_plans,
    workoutProfile?.alerts_plans_use_voice,
    workoutProfileId,
  ]);

  const customAlertListTitle = useMemo(
    () => <Typography variant="prose-base">{t`Custom alerts`}</Typography>,
    []
  );

  const customAlertListItems = useMemo(() => {
    return customAlerts.map(mapAlertToListItem);
  }, [customAlerts, mapAlertToListItem]);

  return {
    editing,
    toggleEditing,
    alertsLoading: workoutProfilesLoading || featuresAreLoading,
    alertsError: workoutProfilesError,
    currentEditorView,
    gotoNextEditorView,
    gotoPreviousEditorView,
    clearEditor,
    editorOpen,
    generalListTitle,
    generalListItems,
    navigationAlertListTitle,
    navigationAlertListItems,
    plannedWorkoutAlertListTitle,
    plannedWorkoutAlertListItems,
    customAlertListTitle,
    customAlertListItems,
    handleAddAlert,
    handleEditAlert,
    currentDrawerHeight,
    setCurrentDrawerHeight,
    showElemntOptions,
    showAceOptions,
    alertUpdateInProgress: updateInProgress,
  };
};

export default useAlertList;
